import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  addDays,
  addMonths,
  format,
  isValid,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns'
import { pl } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/cn'
import CalendarMonthView from '@/features/admin/calendar-month-view'
import CalendarWeekView from '@/features/admin/calendar-week-view'
import {
  WORKING_DAYS_COUNT,
  diffDays,
  getCurrentWeekStart,
  getMondayOfWeek,
  useTodayIso,
} from '@/features/admin/use-calendar-week'

type CalendarView = 'week' | 'month'

/**
 * Walidacja semantyczna, nie tylko kształtu.
 *
 * Sam regex przepuszczał `2026-13-45`, a wtedy `parseISO` zwracał Invalid Date
 * i `format()` z date-fns rzucał `RangeError` w fazie renderu. Aplikacja nie ma
 * ErrorBoundary, więc jeden zły parametr w zakładce albo w historii kasował
 * całe drzewo React i zostawał biały ekran bez nawigacji.
 */
function isValidWeekIso(s: string | null): s is string {
  return (
    !!s &&
    /^\d{4}-\d{2}-\d{2}$/.test(s) &&
    isValid(parseISO(`${s}T00:00:00`))
  )
}

function isValidMonthIso(s: string | null): s is string {
  return (
    !!s && /^\d{4}-\d{2}$/.test(s) && isValid(parseISO(`${s}-01T00:00:00`))
  )
}

const MONTHS_PL_FULL = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
] as const

const MONTHS_PL_SHORT = [
  'Sty',
  'Lut',
  'Mar',
  'Kwi',
  'Maj',
  'Cze',
  'Lip',
  'Sie',
  'Wrz',
  'Paź',
  'Lis',
  'Gru',
] as const

/**
 * Etykieta miesiąca dla tygodnia. Gdy weekStart i weekEnd są w tym samym miesiącu,
 * zwraca „Maj 2026". Gdy zakres przechodzi przez granicę miesięcy:
 *   - ten sam rok → „Maj / Cze 2026"
 *   - różne lata → „Gru 2026 / Sty 2027"
 */
function formatMonthFromWeekStart(weekStart: string): string {
  const start = parseISO(`${weekStart}T00:00:00`)
  const end = addDays(start, WORKING_DAYS_COUNT - 1)
  const m1 = start.getMonth()
  const m2 = end.getMonth()
  const y1 = start.getFullYear()
  const y2 = end.getFullYear()
  if (m1 === m2 && y1 === y2) {
    return `${MONTHS_PL_FULL[m1]} ${y1}`
  }
  if (y1 === y2) {
    return `${MONTHS_PL_SHORT[m1]} / ${MONTHS_PL_SHORT[m2]} ${y2}`
  }
  return `${MONTHS_PL_SHORT[m1]} ${y1} / ${MONTHS_PL_SHORT[m2]} ${y2}`
}

/**
 * Krótki zakres dat tygodnia w formacie maestio: „18.05 — 22.05".
 */
function formatWeekRangeShort(weekStart: string): string {
  const start = parseISO(`${weekStart}T00:00:00`)
  const end = addDays(start, WORKING_DAYS_COUNT - 1)
  const dd1 = String(start.getDate()).padStart(2, '0')
  const mm1 = String(start.getMonth() + 1).padStart(2, '0')
  const dd2 = String(end.getDate()).padStart(2, '0')
  const mm2 = String(end.getMonth() + 1).padStart(2, '0')
  return `${dd1}.${mm1} — ${dd2}.${mm2}`
}

function formatMonthLabel(yearMonth: string): string {
  const d = parseISO(`${yearMonth}-01T00:00:00`)
  const m = format(d, 'LLLL yyyy', { locale: pl })
  return m.charAt(0).toUpperCase() + m.slice(1)
}

const NAV_BUTTON_CLS = cn(
  'inline-flex items-center justify-center rounded-lg transition-colors',
  'h-11 w-11 shrink-0',
  'bg-bg-elevated border border-border-subtle text-text-muted',
  'hover:text-text-primary hover:border-border-strong',
)

export default function AdminCalendar() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const today = useTodayIso()

  /**
   * Podbijany przy każdym kliknięciu „Dziś". Pozycja w kalendarzu żyje w URL,
   * ale „wróć na dzisiaj" to imperatyw — gdy admin JEST już w bieżącym tygodniu,
   * URL się nie zmienia i widok nie miałby na co zareagować. To był powód,
   * dla którego przycisk sprawiał wrażenie zepsutego.
   */
  const [focusToken, setFocusToken] = useState(0)

  const viewParam = searchParams.get('view')
  const view: CalendarView = viewParam === 'month' ? 'month' : 'week'

  const wsParam = searchParams.get('ws')
  const ymParam = searchParams.get('ym')

  const weekStart = isValidWeekIso(wsParam)
    ? getMondayOfWeek(wsParam)
    : getMondayOfWeek(today)
  const yearMonth = isValidMonthIso(ymParam) ? ymParam : today.slice(0, 7)

  const monthCursor = useMemo(
    () => parseISO(`${yearMonth}-01T00:00:00`),
    [yearMonth],
  )

  const offsetFromWeekStart = diffDays(today, weekStart)
  const showsToday =
    view === 'week'
      ? offsetFromWeekStart >= 0 && offsetFromWeekStart < WORKING_DAYS_COUNT
      : yearMonth === today.slice(0, 7)

  function setView(newView: CalendarView) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('view', newView)
        // Kursor wędruje między reprezentacjami zamiast się kasować — inaczej
        // zerknięcie na miesiąc gubiło tydzień, do którego admin doklikał się
        // dziesięcioma strzałkami.
        if (newView === 'month') {
          next.set('ym', weekStart.slice(0, 7))
        } else {
          next.set(
            'ws',
            getMondayOfWeek(
              yearMonth === today.slice(0, 7) ? today : `${yearMonth}-01`,
            ),
          )
        }
        return next
      },
      { replace: false },
    )
  }

  function handlePrev() {
    if (view === 'week') {
      const prev = format(addDays(parseISO(`${weekStart}T00:00:00`), -7), 'yyyy-MM-dd')
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp)
        next.set('view', 'week')
        next.set('ws', prev)
        return next
      })
    } else {
      const prev = format(subMonths(monthCursor, 1), 'yyyy-MM')
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp)
        next.set('view', 'month')
        next.set('ym', prev)
        return next
      })
    }
  }

  function handleNext() {
    if (view === 'week') {
      const nextWs = format(addDays(parseISO(`${weekStart}T00:00:00`), 7), 'yyyy-MM-dd')
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp)
        next.set('view', 'week')
        next.set('ws', nextWs)
        return next
      })
    } else {
      const next = format(addMonths(monthCursor, 1), 'yyyy-MM')
      setSearchParams((sp) => {
        const np = new URLSearchParams(sp)
        np.set('view', 'month')
        np.set('ym', next)
        return np
      })
    }
  }

  function handleToday() {
    // `replace` — „skocz na dziś" to reset pozycji, nie krok nawigacji.
    // Przy push każde kliknięcie dokładało wpis historii z identycznym URL-em
    // i w PWA trzeba było tyleż razy swipe'nąć, żeby wyjść z ekranu.
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp)
        if (view === 'week') {
          next.set('view', 'week')
          next.set('ws', getCurrentWeekStart())
        } else {
          next.set('view', 'month')
          next.set('ym', format(startOfMonth(new Date()), 'yyyy-MM'))
        }
        return next
      },
      { replace: true },
    )
    setFocusToken((t) => t + 1)
  }

  function handleUserClick(date: string, userId: string) {
    navigate(`/admin/day/${date}?user=${userId}`)
  }

  const monthLabel =
    view === 'week' ? formatMonthFromWeekStart(weekStart) : formatMonthLabel(yearMonth)
  const subLabel = view === 'week' ? formatWeekRangeShort(weekStart) : ''

  return (
    <div>
      <div className="mb-5 lg:mb-8 3xl:mb-12">
        <div className="flex items-end justify-between gap-4 mb-3 lg:mb-4">
          <div className="min-w-0">
            <p className="text-[10px] lg:text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5 text-brand-accent">
              Planowanie
            </p>
            <h1 className="text-2xl lg:text-4xl 3xl:text-5xl font-extralight text-text-primary tracking-tight">
              Kalendarz
            </h1>
          </div>

          <div className="inline-flex items-center rounded-pill border border-border-subtle bg-bg-elevated p-0.5 shrink-0">
            <button
              type="button"
              onClick={() => setView('week')}
              className={cn(
                'px-3 py-2 text-xs rounded-pill transition-colors min-h-[36px]',
                view === 'week'
                  ? 'bg-brand-dark text-bg-elevated'
                  : 'text-text-muted hover:text-text-primary',
              )}
              aria-pressed={view === 'week'}
            >
              Tydzień
            </button>
            <button
              type="button"
              onClick={() => setView('month')}
              className={cn(
                'px-3 py-2 text-xs rounded-pill transition-colors min-h-[36px]',
                view === 'month'
                  ? 'bg-brand-dark text-bg-elevated'
                  : 'text-text-muted hover:text-text-primary',
              )}
              aria-pressed={view === 'month'}
            >
              Miesiąc
            </button>
          </div>
        </div>

        {/* Pasek nawigacji w osobnym rzędzie: na 375 px toolbar w jednej linii
            z tytułem miał ~470 px i rozpadał się na trzy poszarpane rzędy. */}
        <div className="flex items-center gap-2 lg:justify-end lg:gap-3">
          <button
            type="button"
            onClick={handlePrev}
            aria-label={view === 'week' ? 'Poprzedni tydzień' : 'Poprzedni miesiąc'}
            className={NAV_BUTTON_CLS}
          >
            <ChevronLeft className="h-[18px] w-[18px]" aria-hidden />
          </button>

          <div
            aria-live="polite"
            className="flex-1 min-w-0 text-center lg:flex-none lg:min-w-[200px]"
          >
            <p className="text-sm sm:text-base lg:text-lg font-medium text-text-primary leading-tight truncate">
              {monthLabel}
            </p>
            {subLabel && (
              <p className="text-[10px] text-text-muted tabular-nums tracking-wider mt-0.5">
                {subLabel}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={handleNext}
            aria-label={view === 'week' ? 'Następny tydzień' : 'Następny miesiąc'}
            className={NAV_BUTTON_CLS}
          >
            <ChevronRight className="h-[18px] w-[18px]" aria-hidden />
          </button>

          <button
            type="button"
            onClick={handleToday}
            aria-label={
              view === 'week'
                ? 'Pokaż bieżący tydzień i dzisiejszy dzień'
                : 'Pokaż bieżący miesiąc'
            }
            className={cn(
              'inline-flex items-center justify-center rounded-lg text-xs font-medium',
              'h-11 px-3 shrink-0 transition-colors border',
              // Gdy dzisiaj jest już na ekranie, przycisk mówi „jesteś tutaj",
              // ale zostaje aktywny — na telefonie wciąż wraca na kartę dnia.
              showsToday
                ? 'bg-brand-accent/10 border-brand-accent/60 text-text-primary'
                : 'bg-bg-elevated border-border-subtle text-text-muted hover:text-text-primary hover:border-border-strong',
            )}
          >
            Dziś
          </button>
        </div>
      </div>

      {view === 'week' ? (
        <CalendarWeekView
          weekStart={weekStart}
          focusToken={focusToken}
          onUserClick={handleUserClick}
        />
      ) : (
        <CalendarMonthView cursor={monthCursor} focusToken={focusToken} />
      )}
    </div>
  )
}
